import React from 'react'
import ReactGA from 'react-ga4'
import { useCookies } from 'react-cookie'
import Icon from 'components/elements/Icon'

import { useActionless, useAppSelector } from 'redux/hooks'

import { VIEW, ViewType } from 'types/deck'
import { SET_DECK_STATE } from 'redux/deck/actions/types'
import { THEME } from 'types/active'

import { setLocalView } from 'services/accountSettings.service'

import styles from './deckLayoutPresenter.module.scss'

type Props = {}

const IMAGES = {
  'light': {
    'stacked': '/images/layouts/card_stacks.webp',
    'grid': '/images/layouts/card_grid.webp',
    'text': '/images/layouts/card_text.webp',
    'table': '/images/layouts/card_table.webp',
  },
  'dark': {
    'stacked': '/images/layouts/card_stacks_dark.webp',
    'grid': '/images/layouts/card_grid_dark.webp',
    'text': '/images/layouts/card_text_dark.webp',
    'table': '/images/layouts/card_table_dark.webp',
  },
}

const DeckLayoutPresenter = ({}: Props) => {
  const [{ tbNeverShowLayouts: closed, theme, tbId: userId }, setCookies] = useCookies([
    'tbNeverShowLayouts',
    'theme',
    'tbId',
  ])

  const view = useAppSelector(state => state.deck.view)

  const [setDeckState] = useActionless(SET_DECK_STATE)

  const handleSetView = (view: ViewType) => {
    setDeckState({ view })
    setLocalView(view)
  }

  const handleClose = () => {
    setCookies('tbNeverShowLayouts', true, { path: '/', expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 365) })
  }

  const dark = `${theme}` === THEME.DARK
  const images = dark ? IMAGES.dark : IMAGES.light

  if (closed || !!userId) return null

  return (
    <div className={styles.container}>
      <p>
        Choose your preferred layout. You can change this at any time using the <span>Deck filters / View as</span>{' '}
        options above.
      </p>

      <div className={styles.content}>
        <Layout
          selected={view === VIEW.CARD}
          src={images.stacked}
          label="stacked"
          onClick={() => handleSetView(VIEW.CARD)}
        />
        <Layout selected={view === VIEW.GRID} src={images.grid} label="grid" onClick={() => handleSetView(VIEW.GRID)} />
        <Layout selected={view === VIEW.TEXT} src={images.text} label="text" onClick={() => handleSetView(VIEW.TEXT)} />
        <Layout
          selected={view === VIEW.SPREADSHEET}
          src={images.table}
          label="table"
          onClick={() => handleSetView(VIEW.SPREADSHEET)}
        />
      </div>

      <button className={styles.closeButton} onClick={handleClose}>
        <Icon name="close" />
      </button>
    </div>
  )
}

export default DeckLayoutPresenter

const Layout = ({
  src,
  label,
  onClick,
  selected = false,
}: {
  src: string
  label: string
  onClick: () => void
  selected?: boolean
}) => (
  <div className={styles.layout}>
    <button className={styles.imageButton} onClick={onClick}>
      <img src={src} alt={`${label} view`} className={selected ? styles.selected : undefined} />
    </button>
    <input checked={selected} type="radio" className={styles.selectButton} onClick={onClick} onChange={() => null} />
  </div>
)
